import React from "react";

import { FlexColumn } from "components/FlexColumn";
import Header from "components/Header";
import Loader from "components/Loader";
import Markdown from "components/Markdown";
import HelpBar, { PDFDownload } from "components/projects/HelpBar";
import SubHeader from "components/projects/requirement/SubHeader";
import Footer from "components/projects/requirement/SummaryFooter";
import { AnswerContextWithContext } from "contexts/AnswerContextProvider";
import { useAnswerContext } from "contexts/answerContext";
import useParam from "hooks/useParam";
import {
  useDownloadApplicationPDF,
  useDownloadIssuedApplicationPDF,
} from "queries/requirementApplications";
import { useRequirementBySlug } from "queries/requirements";
import { getRequirementApplicationByRequirementID } from "reducers/projects";
import { getCurrentState } from "reducers/requirementApplications";
import { getNextStepsMarkdown, getRequirementStepCount } from "reducers/requirements";

import Step from "./Step";
import RequirementSummary from "./Summary";
import styles from "./Summary.scss";

const IssuedPDFDownloadButtton = () => {
  const { record: project, context: requirement } = useAnswerContext();
  const application = project && getRequirementApplicationByRequirementID(project, requirement?.id);
  const mutation = useDownloadIssuedApplicationPDF(application.id);

  return <PDFDownload mutation={mutation} />;
};

const ApplicationPDFDownloadButtton = () => {
  const { record: project, context: requirement } = useAnswerContext();
  const application = project && getRequirementApplicationByRequirementID(project, requirement?.id);
  const mutation = useDownloadApplicationPDF(application.id);

  return <PDFDownload mutation={mutation} />;
};

const SummaryHelpBar = ({ state }) => {
  const PDFDownloadButton =
    state === "issued" ? IssuedPDFDownloadButtton : ApplicationPDFDownloadButtton;

  return <HelpBar DownloadButton={PDFDownloadButton} showPrint />;
};

const RequirementSummaryPage = () => {
  const { record: project } = useAnswerContext();
  const requirementSlug = useParam("requirementSlug");
  const { data: requirement, isLoading } = useRequirementBySlug(requirementSlug);
  if (isLoading) return <Loader />;

  const steps = getRequirementStepCount(requirement);
  const application = project && getRequirementApplicationByRequirementID(project, requirement?.id);
  const state = getCurrentState(application || {});
  if (!application) return <Loader />;

  const isIssued = state === "issued";

  const reviewType = requirement.review_type.toLowerCase();
  const verb = isIssued ? reviewType : "submitted";
  const suffix = requirement.type === "Upload" ? "_upload" : "";

  return (
    <AnswerContextWithContext context={requirement}>
      <FlexColumn fullHeight>
        <div className={styles.header}>
          <Header hideTitle />
          <SubHeader requirement={requirement} />
        </div>
        <FlexColumn exactHeightContent className={styles.container}>
          <div className={styles.gutters}>
            <main role="main" className={styles.contentContainer}>
              <Step
                headerKey={`projects.requirement.summary.${verb}_title${suffix}`}
                instructions={isIssued ? "" : requirement.nextSteps}
                step={steps}
                steps={steps}
              />
              {!isIssued && <Markdown source={getNextStepsMarkdown(requirement)} />}
              <div className={styles.requirementContainer}>
                <RequirementSummary
                  requirement={requirement}
                  application={application}
                  state={state}
                />
              </div>
              <SummaryHelpBar state={state} />
            </main>
          </div>
        </FlexColumn>
        <Footer requirementID={requirement.id} />
      </FlexColumn>
    </AnswerContextWithContext>
  );
};

export default RequirementSummaryPage;
